/**
 * Zwarte grond prijscalculator — storefront logica (geporte, geteste calculator; JS-hooks/selectors bewust jtw-genaamd, logica ongewijzigd).
 *
 * Leest het camelCase `frontend`-contract van POST /api/pricing/calculate en
 * toont één van vijf states:
 *   loading | priced | manual_quote_required | invalid_input/invalid_quantity | error
 *
 * De BACKEND rekent alles uit. Deze JS rekent zelf niets uit, toont nooit
 * inkoopprijs of marge, en zet line-item properties + afrekenknop alleen als
 * `canCheckout === true`.
 *
 * Afrekenen (draft order): bij een afrekenbare prijs verschijnt een e-mailveld
 * + afrekenknop. Bij klik POST't de JS { quote_id, email } naar de
 * checkout-URL (data-checkout-url -> POST /api/checkout/create) en stuurt de
 * klant door naar de teruggegeven invoice_url. De quote_id komt UITSLUITEND uit
 * de laatste can_checkout=true calculate-response; bij elke invoerwijziging
 * vervalt die (stale-quote-vangnet), zodat afrekenen altijd hoort bij de op dat
 * moment getoonde prijs.
 */
(function () {
  "use strict";

  /**
   * Uppercase, strip spaties/punten/streepjes, en valideer zoals de engine
   * (normalizeCustomerPostcode): 1234AB, met VERPLICHT twee letters. "1234"
   * alleen keurt de engine af, dus de calculator ook.
   */
  function normalizePostcode(raw) {
    var cleaned = String(raw || "").toUpperCase().replace(/[\s.\-_]/g, "");
    var match = cleaned.match(/^([1-9]\d{3})([A-Z]{2})$/);
    return match ? { valid: true, value: cleaned, pc4: match[1] } : { valid: false, value: cleaned, pc4: null };
  }

  function formatMoney(amount, currency) {
    try {
      return new Intl.NumberFormat("nl-NL", { style: "currency", currency: currency || "EUR" }).format(amount);
    } catch (_e) {
      return "€ " + Number(amount).toFixed(2).replace(".", ",");
    }
  }

  function packageLabel(pkg) {
    return pkg === "big_bag" ? "Big bag" : "Los gestort";
  }

  /** Eenvoudige client-side e-mailcheck; de server (zod .email) valideert nogmaals. */
  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
  }

  /* ---------------------------------------------------------------------
   * Meting (GA4 via Shopify Customer Events)
   *
   * De calculator publiceert zes gebeurtenissen met Shopify.analytics.publish
   * (postcode_ingevuld, prijs_getoond, postcode_geen_levering, offerte_op_maat,
   * calculator_fout, bestelling_aangevraagd);
   * een custom pixel in de winkel zet ze door naar GA4. Stand 18-10-2026:
   *
   *   postcode_ingevuld        postcode_regio, product, pagina_type. Hoogstens
   *                            één keer per postcode per calculator.
   *   prijs_getoond            postcode_regio, product, hoeveelheid_m3, prijs,
   *                            verpakking, zone (als bekend), pagina_type.
   *   postcode_geen_levering   postcode_regio, product, reden, pagina_type.
   *                            Offerte zonder vaste redencode van "op maat":
   *                            buiten_gebied, geen_tarief, handmatige_offerte, overig.
   *   offerte_op_maat          postcode_regio, product, reden, pagina_type. Reden
   *                            knijper, waddeneilanden, boven_max_m3 of
   *                            product_offerte; of op_aanvraag als de engine
   *                            opAanvraag=true meegeeft (levering alleen op
   *                            aanvraag, ongeacht de reasonCode).
   *   calculator_fout          postcode_regio, product, reden (engine_fout_<status>
   *                            of engine_onbereikbaar), pagina_type.
   *   bestelling_aangevraagd   zie de afrekenstap verderop.
   *
   * Ontdubbelen: prijs_getoond, postcode_geen_levering, offerte_op_maat en
   * calculator_fout gaan elk hoogstens één keer uit per product + postcode (vier
   * cijfers) + m3 + verpakking per paginaweergave (module-scope, gedeeld door alle
   * calculators op de pagina, eigen sleutelruimte per gebeurtenis). Dezelfde
   * berekening opnieuw levert dus geen tweede gebeurtenis; een andere hoeveelheid,
   * verpakking of postcode wel. Twee harde regels:
   *
   *   1. Meten mag de calculator NOOIT breken. Bestaat Shopify.analytics.publish
   *      niet, dan gebeurt er simpelweg niets; elke aanroep zit in een try/catch.
   *   2. Nooit een volledige postcode, klantgegevens (e-mail), leveranciersnaam,
   *      inkoopprijs of marge. Alleen de vier postcodecijfers, het product, de
   *      hoeveelheid, de getoonde verkoopprijs en het postcodegebied. `reden` is
   *      altijd een vaste code (de reasonCode van de engine, zie reasonFor, of
   *      een engine_fout-/engine_onbereikbaar-code), nooit vrije tekst.
   *
   * Elke gebeurtenis krijgt ook `pagina_type` mee (homepage | werkgebied |
   * product | overig), uit het data-pagina-type-attribuut dat de snippet zet.
   * ------------------------------------------------------------------- */

  /** Publiceer een gebeurtenis. Kan er niet gemeten worden, dan gebeurt er niets. */
  function track(eventName, params) {
    try {
      var analytics = window.Shopify && window.Shopify.analytics;
      if (!analytics || typeof analytics.publish !== "function") return;
      var published = analytics.publish(eventName, params);
      // publish() geeft een belofte terug; een niet-afgeleverde gebeurtenis mag
      // geen unhandled rejection in de console opleveren.
      if (published && typeof published.catch === "function") published.catch(function () {});
    } catch (_e) {
      /* meten mag de calculator nooit breken */
    }
  }

  /** De toegestane waarden voor `pagina_type`; al het andere wordt `overig`. */
  var PAGE_TYPES = ["homepage", "werkgebied", "product", "overig"];

  /** Het paginatype van deze calculator, altijd een waarde uit PAGE_TYPES. */
  function pageTypeOf(root) {
    var value = String(root.getAttribute("data-pagina-type") || "").trim().toLowerCase();
    return PAGE_TYPES.indexOf(value) === -1 ? "overig" : value;
  }

  /** "1234-1299" uit het postcodegebied van de engine, of null als dat ontbreekt. */
  function zoneLabel(area) {
    if (!area || typeof area.from !== "number" || typeof area.to !== "number") return null;
    return area.from + "-" + area.to;
  }

  /**
   * De vaste redencodes bij een handmatige offerte. De engine geeft de code zelf
   * mee (frontend.reasonCode); de calculator leest de klanttekst nooit uit.
   *
   *   buiten_gebied    geen automatische prijs voor deze postcode
   *   geen_tarief      geen prijs voor deze verpakking en hoeveelheid
   *   boven_max_m3     boven het maximum (50 m³)
   *   product_offerte  dit product gaat altijd via een offerte
   *   waddeneilanden   postcode op de Waddeneilanden: altijd een offerte (besluit #15)
   */
  var REASON_CODES = ["buiten_gebied", "geen_tarief", "boven_max_m3", "product_offerte", "waddeneilanden", "knijper"];

  /**
   * De redencodes die een offerte op maat zijn (bewust gekozen of vereist): die gaan
   * als `offerte_op_maat` naar GA4. De overige offertes (buiten_gebied, geen_tarief,
   * handmatige_offerte, overig) blijven `postcode_geen_levering`.
   */
  var OFFERTE_OP_MAAT_CODES = ["knijper", "waddeneilanden", "boven_max_m3", "product_offerte"];

  /**
   * De redencode van een frontend-antwoord, voor GA4 (`reden`) en de
   * offerteaanvraag. Geeft de engine geen code mee, dan `handmatige_offerte`
   * (we weten dat het een offerte is, maar niet waarom); een onbekende code
   * wordt `overig`.
   */
  function reasonFor(fe) {
    var code = fe ? fe.reasonCode : null;
    if (code === null || code === undefined || code === "") return "handmatige_offerte";
    return REASON_CODES.indexOf(code) === -1 ? "overig" : code;
  }

  /**
   * `prijs_getoond`, `postcode_geen_levering`, `offerte_op_maat` en `calculator_fout`
   * gaan elk hoogstens één keer uit per product + postcode + m3 + verpakking per
   * paginaweergave. Module-scope, dus gedeeld door alle calculators op de pagina:
   * opnieuw berekenen met dezelfde invoer levert geen tweede gebeurtenis op. De
   * sleutel bevat de gebeurtenisnaam, dus elke gebeurtenis heeft een eigen ruimte.
   */
  var reportedEvents = Object.create(null);

  /** true = al gemeld; anders de combinatie markeren en false teruggeven. */
  function alreadyReported(eventName, product, pc4, quantity, packageType) {
    var key = eventName + "|" + product + "|" + pc4 + "|" + quantity + "|" + packageType;
    if (reportedEvents[key]) return true;
    reportedEvents[key] = true;
    return false;
  }

  /**
   * Anonieme sessie-id per tabblad (sessionStorage), voor het ontdubbelen in
   * Blindspots: de engine telt herhaalde berekeningen van dezelfde bezoeker op
   * dezelfde dag als een vraag. Een willekeurige id: geen IP, geen browserinfo,
   * geen andere persoonsgegevens. Gaat mee als body-veld session_id; een
   * onbekend of ongeldig veld geeft bij de engine nooit een fout. Lukt het niet
   * (opslag geblokkeerd, oude browser), dan null: de calculator werkt gewoon en
   * die berekening telt los.
   */
  var SESSIE_SLEUTEL = "wg-calc-sessie";
  function calculatorSessionId() {
    try {
      var id = window.sessionStorage.getItem(SESSIE_SLEUTEL);
      if (id && /^[A-Za-z0-9-]{8,64}$/.test(id)) return id;
      var c = window.crypto;
      if (c && typeof c.randomUUID === "function") {
        id = c.randomUUID();
      } else if (c && typeof c.getRandomValues === "function") {
        id = Array.prototype.map.call(c.getRandomValues(new Uint8Array(16)), function (b) {
          return ("0" + b.toString(16)).slice(-2);
        }).join("");
      } else {
        return null;
      }
      window.sessionStorage.setItem(SESSIE_SLEUTEL, id);
      return id;
    } catch (_e) {
      return null;
    }
  }

  /**
   * Het bezoek waarmee de klant in de winkel kwam (layout/theme.liquid zet het
   * in sessionStorage "wg-herkomst"). Gaat mee als body-veld herkomst, alleen
   * voor de telling bij de engine; de prijs verandert er niet door en een
   * ontbrekend of raar veld geeft bij de engine nooit een fout. Lukt lezen niet
   * (opslag geblokkeerd, oude browser), dan null: de calculator werkt gewoon.
   */
  function calculatorHerkomst() {
    try {
      var ruw = window.sessionStorage.getItem("wg-herkomst");
      var herkomst = ruw ? JSON.parse(ruw) : null;
      return herkomst && typeof herkomst === "object" && !Array.isArray(herkomst) ? herkomst : null;
    } catch (_e) {
      return null;
    }
  }

  /**
   * Klantvriendelijke melding per checkout-statuscode (zie src/api/routes/checkout.ts).
   * Toont NOOIT ruwe serverdetails, leverancier, inkoopprijs of marge.
   */
  function checkoutErrorMessage(status) {
    switch (status) {
      case 400:
        return "Controleer uw e-mailadres en probeer het opnieuw.";
      case 404:
      case 410:
        return "Deze prijs is verlopen. Bereken de prijs opnieuw.";
      case 409:
        return "Voor deze berekening is al een bestelling aangemaakt.";
      case 422:
        return "Deze prijs is niet direct afrekenbaar. Vraag een offerte aan.";
      case 503:
        return "Afrekenen is tijdelijk niet beschikbaar. Probeer het later opnieuw.";
      default:
        return "Afrekenen lukte niet. Probeer het later opnieuw of vraag een offerte aan.";
    }
  }

  /**
   * Vul verborgen line-item properties. De SERVER bepaalt welke velden dit zijn
   * (line_item_properties): een opaque _quote_id + niet-gevoelige velden. De
   * leverancier, inkoopprijs en marge zitten hier NOOIT in; we forwarden 1-op-1.
   */
  function setMetadata(container, props) {
    container.innerHTML = "";
    if (!props) return;
    Object.keys(props).forEach(function (key) {
      var value = props[key];
      if (value === null || value === undefined) return;
      var input = document.createElement("input");
      input.type = "hidden";
      input.name = "properties[" + key + "]";
      input.value = String(value);
      container.appendChild(input);
    });
  }

  /* ---------------------------------------------------------------------
   * De hoeveelheidsladder (besluit 22-09-2026). Spiegelt src/lib/quantity.ts
   * van de prijsengine: ALLEEN HELE KUUBS, minimaal 1 m3, automatisch geprijsd
   * t/m 50, daarboven een handmatige offerte. De server blijft leidend - dit is
   * alleen om de klant niet iets te laten invullen dat toch geweigerd wordt.
   * ------------------------------------------------------------------- */

  /**
   * Lees een getal zoals een klant het typt: "2,5", "2.5" en " 3 " worden 2.5,
   * 2.5 en 3. Alles wat geen gewoon getal is ("abc", "2,5,1", "-2", leeg) wordt
   * NaN. Eén parser voor het kuubveld, de knoppen, de m³-hulp en de grenzen,
   * zodat er nooit een parseFloat("2,5") = 2 naar de engine gaat.
   */
  function parseDecimal(raw) {
    var tekst = String(raw == null ? "" : raw).trim().replace(",", ".");
    return /^(\d+(\.\d*)?|\.\d+)$/.test(tekst) ? Number(tekst) : NaN;
  }

  /** Een grens van het kuubveld (data-jtw-min / data-jtw-max), anders de standaard. */
  function quantityBound(input, attr, fallback) {
    var v = input ? parseDecimal(input.getAttribute(attr)) : NaN;
    return isFinite(v) && v > 0 ? v : fallback;
  }

  /** De eerstvolgende hele kuub omhoog. */
  function quantityUp(q, min) {
    if (!isFinite(q)) return min;
    if (q < min) return min;
    return Math.floor(q) + 1;
  }

  /** De eerstvolgende hele kuub omlaag, nooit onder het minimum. */
  function quantityDown(q, min) {
    if (!isFinite(q)) return min;
    return Math.max(min, Math.ceil(q) - 1);
  }

  /**
   * Trek een ingetypte waarde naar een hele kuub, naar BOVEN: je wilt nooit te
   * weinig materiaal. Boven het maximum blijft de waarde gewoon staan; daar
   * hoort een handmatige offerte uit te komen, en die vraag mag de klant
   * gewoon stellen.
   */
  function snapQuantity(q, min) {
    if (!isFinite(q) || q <= 0) return min;
    if (q < min) return min;
    return Math.max(min, Math.ceil(q));
  }

  /** "1,5" voor in een zin. */
  function qtyLabel(n) {
    return String(n).replace(".", ",");
  }

  /**
   * Corrigeer een ingetypt aantal naar de ladder, met uitleg voor de klant.
   * Geeft { value, note }; note is leeg als er niets te corrigeren viel. Nooit
   * stil doorsturen: de klant moet zien dat er iets anders staat dan hij typte.
   */
  function correctQuantity(raw, min) {
    var getypt = parseDecimal(raw);
    if (!isFinite(getypt) || getypt <= 0) {
      return { value: min, note: "Vul een aantal kuub in. We zijn uitgegaan van " + qtyLabel(min) + " m³." };
    }
    var gecorrigeerd = snapQuantity(getypt, min);
    if (Math.abs(gecorrigeerd - getypt) < 1e-9) return { value: gecorrigeerd, note: "" };
    return {
      value: gecorrigeerd,
      note:
        getypt < min
          ? "We leveren vanaf " + qtyLabel(min) + " m³, dus we hebben er " + qtyLabel(gecorrigeerd) + " m³ van gemaakt."
          : qtyLabel(getypt) + " m³ kan niet, we leveren per hele kuub; we hebben er " +
            qtyLabel(gecorrigeerd) + " m³ van gemaakt.",
    };
  }

  function els(root) {
    return {
      postcode: root.querySelector("[data-jtw-postcode]"),
      postcodeError: root.querySelector("[data-jtw-postcode-error]"),
      quantity: root.querySelector("[data-jtw-quantity]"),
      qtyUp: root.querySelector("[data-jtw-qty-up]"),
      qtyDown: root.querySelector("[data-jtw-qty-down]"),
      quantityNote: root.querySelector("[data-jtw-quantity-note]"),
      pkg: root.querySelector("[data-jtw-package]"),
      knijper: root.querySelector("[data-jtw-knijper]"),
      knijperField: root.querySelector("[data-jtw-knijper-field]"),
      btn: root.querySelector("[data-jtw-calculate]"),
      btnLabel: root.querySelector("[data-jtw-button-label]"),
      spinner: root.querySelector("[data-jtw-spinner]"),
      result: root.querySelector("[data-jtw-result]"),
      message: root.querySelector("[data-jtw-message]"),
      priceWrap: root.querySelector("[data-jtw-price-wrap]"),
      price: root.querySelector("[data-jtw-price]"),
      meta: root.querySelector("[data-jtw-meta]"),
      perM3: root.querySelector("[data-jtw-per-m3]"),
      deliveryField: root.querySelector("[data-jtw-delivery-field]"),
      deliveryDate: root.querySelector("[data-jtw-delivery-date]"), // verborgen YYYY-MM-DD -> checkout
      deliveryError: root.querySelector("[data-jtw-delivery-error]"),
      // Eigen kalender-widget (vervangt de native <input type=date>).
      deliveryDisplay: root.querySelector("[data-jtw-delivery-display]"),
      deliveryDisplayText: root.querySelector("[data-jtw-delivery-display-text]"),
      deliveryPopup: root.querySelector("[data-jtw-delivery-popup]"),
      calPrev: root.querySelector("[data-jtw-cal-prev]"),
      calNext: root.querySelector("[data-jtw-cal-next]"),
      calTitle: root.querySelector("[data-jtw-cal-title]"),
      calGrid: root.querySelector("[data-jtw-cal-grid]"),
      emailField: root.querySelector("[data-jtw-email-field]"),
      email: root.querySelector("[data-jtw-email]"),
      emailError: root.querySelector("[data-jtw-email-error]"),
      checkout: root.querySelector("[data-jtw-checkout]"),
      checkoutLabel: root.querySelector("[data-jtw-checkout-label]"),
      checkoutSpinner: root.querySelector("[data-jtw-checkout-spinner]"),
      quote: root.querySelector("[data-jtw-quote]"),
      metadata: root.querySelector("[data-jtw-metadata]"),
      m3Details: root.querySelector("[data-jtw-m3]"),
      m3Area: root.querySelector("[data-jtw-m3-area]"),
      m3Depth: root.querySelector("[data-jtw-m3-depth]"),
      m3Result: root.querySelector("[data-jtw-m3-result]"),
      m3Value: root.querySelector("[data-jtw-m3-value]"),
      m3Over: root.querySelector("[data-jtw-m3-over]"),
      m3Apply: root.querySelector("[data-jtw-m3-apply]"),
    };
  }

  /** Toon/verberg de foutregel onder het e-mailveld; markeert het veld als invalid. */
  function setEmailError(e, msg) {
    if (e.emailError) {
      e.emailError.textContent = msg || "";
      e.emailError.hidden = !msg;
      e.emailError.classList.toggle("jtw-price-calculator__hint--error", !!msg);
    }
    if (e.email) e.email.setAttribute("aria-invalid", msg ? "true" : "false");
  }

  /** Toon/verberg de foutregel onder het leverdatum-veld; markeert het veld als invalid. */
  function setDeliveryError(e, msg) {
    if (e.deliveryError) {
      e.deliveryError.textContent = msg || "";
      e.deliveryError.hidden = !msg;
    }
    // aria-invalid op het zichtbare weergaveveld (de verborgen input toont niets).
    if (e.deliveryDisplay) e.deliveryDisplay.setAttribute("aria-invalid", msg ? "true" : "false");
  }

  // ---- Eigen kalender-widget ----------------------------------------------
  // Vervangt de native <input type=date>. Alleen de door de engine geleverde
  // leverbare dagen (fe.deliveryDays) zijn klikbaar; al het andere is grijs en
  // onklikbaar. De widget rekent NIETS zelf uit — hij tekent alleen de set die
  // de engine gaf. De server blijft bij checkout de poortwachter.

  var MONTHS_NL = ["januari","februari","maart","april","mei","juni","juli","augustus","september","oktober","november","december"];
  var WEEKDAYS_NL = ["zondag","maandag","dinsdag","woensdag","donderdag","vrijdag","zaterdag"];

  /** "YYYY-MM-DD" -> {y,m,d} (m is 1-12). */
  function parseISO(iso) {
    var p = String(iso || "").split("-");
    return { y: +p[0], m: +p[1], d: +p[2] };
  }
  /** {y,m} -> maand-sleutel "YYYY-MM" voor vergelijken. */
  function monthKey(y, m) {
    return y + "-" + String(m).padStart(2, "0");
  }
  /** "YYYY-MM-DD" -> "woensdag 8 juli 2026" (leesbaar, tijdzone-veilig via UTC-middag). */
  function humanDate(iso) {
    var p = parseISO(iso);
    var wd = new Date(Date.UTC(p.y, p.m - 1, p.d, 12)).getUTCDay();
    return WEEKDAYS_NL[wd] + " " + p.d + " " + MONTHS_NL[p.m - 1] + " " + p.y;
  }
  /** Weekdag van de 1e van een maand, ma=0..zo=6 (voor de grid-offset). */
  function firstWeekdayMon0(y, m) {
    var wd = new Date(Date.UTC(y, m - 1, 1, 12)).getUTCDay(); // zo=0..za=6
    return (wd + 6) % 7; // ma=0..zo=6
  }
  function daysInMonth(y, m) {
    return new Date(Date.UTC(y, m, 0, 12)).getUTCDate();
  }

  /**
   * Bouw een kalender-controller voor één calculator. Houdt eigen state
   * (beschikbare dagen, zichtbare maand, gekozen dag) en tekent de grid.
   */
  function createCalendar(e, onPick) {
    var available = Object.create(null); // set van klikbare "YYYY-MM-DD"
    var minKey = null;
    var maxKey = null;
    var viewY = 0;
    var viewM = 0; // 1-12
    var selected = null;
    var open = false;

    function hasDays() {
      return minKey !== null;
    }

    /** Zet de klikbare dagen (uit de engine-respons) en reset de weergave. */
    function configure(days) {
      available = Object.create(null);
      (days || []).forEach(function (d) {
        available[d] = true;
      });
      selected = null;
      if (days && days.length) {
        var first = parseISO(days[0]);
        var last = parseISO(days[days.length - 1]);
        minKey = monthKey(first.y, first.m);
        maxKey = monthKey(last.y, last.m);
        viewY = first.y;
        viewM = first.m;
      } else {
        minKey = maxKey = null;
      }
      if (e.deliveryDate) e.deliveryDate.value = "";
      if (e.deliveryDisplayText) e.deliveryDisplayText.textContent = "Kies een datum…";
      close();
    }

    function reset() {
      configure([]);
    }

    function iso(y, m, d) {
      return y + "-" + String(m).padStart(2, "0") + "-" + String(d).padStart(2, "0");
    }

    function pick(dateIso) {
      selected = dateIso;
      if (e.deliveryDate) e.deliveryDate.value = dateIso;
      if (e.deliveryDisplayText) e.deliveryDisplayText.textContent = humanDate(dateIso);
      if (onPick) onPick(dateIso);
      close();
      if (e.deliveryDisplay) e.deliveryDisplay.focus();
    }

    /** Teken de zichtbare maand in het grid. */
    function render() {
      if (!e.calGrid) return;
      if (e.calTitle) e.calTitle.textContent = MONTHS_NL[viewM - 1] + " " + viewY;
      if (e.calPrev) e.calPrev.disabled = monthKey(viewY, viewM) <= minKey;
      if (e.calNext) e.calNext.disabled = monthKey(viewY, viewM) >= maxKey;

      var html = "";
      var offset = firstWeekdayMon0(viewY, viewM);
      for (var i = 0; i < offset; i++) {
        html += '<span class="jtw-price-calculator__day jtw-price-calculator__day--empty" aria-hidden="true"></span>';
      }
      var total = daysInMonth(viewY, viewM);
      for (var day = 1; day <= total; day++) {
        var dIso = iso(viewY, viewM, day);
        var clickable = available[dIso] === true;
        var isSel = dIso === selected;
        var cls = "jtw-price-calculator__day" + (clickable ? "" : " jtw-price-calculator__day--disabled") + (isSel ? " jtw-price-calculator__day--selected" : "");
        html +=
          '<button type="button" role="gridcell" class="' + cls + '"' +
          ' data-jtw-day="' + dIso + '"' +
          (clickable ? "" : " disabled aria-disabled=\"true\"") +
          (isSel ? ' aria-selected="true"' : "") +
          ' tabindex="' + (clickable ? "0" : "-1") + '">' + day + "</button>";
      }
      e.calGrid.innerHTML = html;
    }

    function show() {
      if (!hasDays() || !e.deliveryPopup) return;
      open = true;
      setHidden(e.deliveryPopup, false);
      if (e.deliveryDisplay) e.deliveryDisplay.setAttribute("aria-expanded", "true");
      render();
      // Focus de eerste klikbare dag voor toetsenbordgebruik.
      var firstDay = e.calGrid && e.calGrid.querySelector('[data-jtw-day]:not([disabled])');
      if (firstDay) firstDay.focus({ preventScroll: true });
      reveal();
    }

    /**
     * Schuif de open kalender in beeld, boven een vaste balk onderin (de
     * cookiemelding van Shopify, de mobiele bestelbalk). Zonder dit vallen de
     * onderste weekrijen eronder en zijn die dagen niet aan te klikken.
     */
    function reveal() {
      var popup = e.deliveryPopup;
      if (!popup) return;
      var r = popup.getBoundingClientRect();
      var cover = window.innerHeight;
      // Het hele zichtbare deel van de kalender aflopen, op drie punten in de
      // breedte. Zo tellen ook gestapelde balken mee, en een zwevende balk met
      // een vrije rand eronder. Alleen balken die lager beginnen dan de
      // bovenkant van de kalender tellen.
      var onder = Math.min(r.bottom, window.innerHeight) - 1;
      for (var y = onder; y > Math.max(r.top, 0); y -= 12) {
        [0.2, 0.5, 0.8].forEach(function (fx) {
          var hit = document.elementFromPoint(r.left + r.width * fx, y);
          if (!hit || popup.contains(hit)) return;
          for (var n = hit; n && n !== document.body; n = n.parentElement) {
            var pos = getComputedStyle(n).position;
            if (pos === "fixed" || pos === "sticky") {
              var top = n.getBoundingClientRect().top;
              if (top > r.top) cover = Math.min(cover, top);
              break;
            }
          }
        });
      }
      var marge = 8;
      // Niet verder schuiven dan de bovenkant van de kalender toelaat.
      var omlaag = Math.min(r.bottom + marge - cover, r.top - marge);
      if (omlaag > 0) window.scrollBy(0, omlaag);
    }

    function close() {
      open = false;
      if (e.deliveryPopup) setHidden(e.deliveryPopup, true);
      if (e.deliveryDisplay) e.deliveryDisplay.setAttribute("aria-expanded", "false");
    }

    function toggle() {
      if (open) close();
      else show();
    }

    function step(delta) {
      var y = viewY;
      var m = viewM + delta;
      if (m < 1) { m = 12; y--; }
      if (m > 12) { m = 1; y++; }
      var k = monthKey(y, m);
      if (k < minKey || k > maxKey) return;
      viewY = y;
      viewM = m;
      render();
      // Een maand met zes weekrijen is hoger: opnieuw boven de balken onderin.
      reveal();
    }

    // -- Events --
    if (e.deliveryDisplay) {
      e.deliveryDisplay.addEventListener("click", toggle);
    }
    if (e.calPrev) e.calPrev.addEventListener("click", function () { step(-1); });
    if (e.calNext) e.calNext.addEventListener("click", function () { step(1); });

    // Klik op een klikbare dag (event-delegatie op het grid).
    if (e.calGrid) {
      e.calGrid.addEventListener("click", function (ev) {
        var btn = ev.target.closest ? ev.target.closest("[data-jtw-day]") : null;
        if (btn && !btn.disabled) pick(btn.getAttribute("data-jtw-day"));
      });
      // Toetsenbord binnen het grid: pijltjes verplaatsen focus, Enter/Space kiest.
      e.calGrid.addEventListener("keydown", function (ev) {
        var cur = ev.target.closest ? ev.target.closest("[data-jtw-day]") : null;
        if (!cur) return;
        var move = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: 7, ArrowUp: -7 };
        if (ev.key in move) {
          ev.preventDefault();
          var cells = Array.prototype.slice.call(e.calGrid.querySelectorAll("[data-jtw-day]"));
          var idx = cells.indexOf(cur);
          var next = cells[idx + move[ev.key]];
          if (next) next.focus();
        } else if (ev.key === "Enter" || ev.key === " ") {
          ev.preventDefault();
          if (!cur.disabled) pick(cur.getAttribute("data-jtw-day"));
        } else if (ev.key === "Escape") {
          close();
          if (e.deliveryDisplay) e.deliveryDisplay.focus();
        }
      });
    }
    // Klik buiten de popup sluit hem.
    document.addEventListener("click", function (ev) {
      if (!open) return;
      var picker = e.deliveryDisplay && e.deliveryDisplay.closest("[data-jtw-datepicker]");
      if (picker && !picker.contains(ev.target)) close();
    });

    return { configure: configure, reset: reset, close: close, hasDays: hasDays };
  }

  /**
   * Loading-state van de afrekenknop. Spiegelt setLoading/setHidden: disable de
   * knop en toon de spinner robuust via inline display (wint van Dawn .button-CSS).
   */
  function setCheckoutLoading(e, loading) {
    if (e.checkout) e.checkout.disabled = loading;
    if (e.checkoutSpinner) {
      e.checkoutSpinner.hidden = !loading;
      e.checkoutSpinner.style.display = loading ? "inline-block" : "none";
    }
  }

  /**
   * Verberg/toon een element ROBUUST. Let op: het HTML-attribuut `hidden` werkt
   * via de UA-regel [hidden]{display:none}, maar die verliest van ELKE author-CSS
   * die `display` zet — zoals de thema-class .product__price--holder{display:flex}
   * en onze eigen .jtw-price-calculator__spinner{display:inline-block}. Daarom
   * forceren we ook een inline display; inline styles winnen van class-CSS.
   */
  function setHidden(el, hidden) {
    if (!el) return;
    el.hidden = hidden;
    el.style.display = hidden ? "none" : "";
  }

  function setLoading(e, loading) {
    e.btn.disabled = loading;
    // De spinner heeft eigen CSS (display:inline-block); `hidden` alleen verbergt
    // 'm dus niet. Forceer met inline display, zodat de knop nooit blijft "laden".
    if (e.spinner) {
      e.spinner.hidden = !loading;
      e.spinner.style.display = loading ? "inline-block" : "none";
    }
  }

  /**
   * Reset de VOLLEDIGE vorige uitkomst. Cruciaal: zonder dit blijft de prijs van
   * een vorige (priced) postcode staan wanneer de volgende postcode geen
   * automatische prijs geeft (manual_quote). We wissen zowel de tekst als de
   * weergave (robuust verbergen) en de verborgen line-item properties.
   */
  function resetResult(e) {
    e.price.textContent = "";
    e.meta.textContent = "";
    if (e.perM3) setHidden(e.perM3, true);
    setHidden(e.priceWrap, true);
    if (e.checkout) setHidden(e.checkout, true);
    setCheckoutLoading(e, false);
    if (e.deliveryField) setHidden(e.deliveryField, true);
    setDeliveryError(e, "");
    if (e.emailField) setHidden(e.emailField, true);
    setEmailError(e, "");
    if (e.quote) setHidden(e.quote, true);
    setMetadata(e.metadata, null);
  }

  /** Render een state. `state` = priced|manual_quote_required|invalid_input|invalid_quantity|error */
  function render(e, fe, opts) {
    opts = opts || {};
    // Begin ALTIJD schoon: elk antwoordtype zet hierna zijn eigen weergave volledig,
    // zodat er nooit een oude prijs of knop van een vorige berekening blijft staan.
    resetResult(e);
    e.result.hidden = false;
    e.result.setAttribute("data-state", fe.status);
    e.message.textContent = fe.message || "";

    var isPriced = fe.status === "priced";
    if (isPriced) {
      e.price.textContent = formatMoney(fe.salePrice, fe.currency);
      var bits = [];
      if (fe.quantity) bits.push(fe.quantity + " m³");
      if (opts.packageLabel) bits.push(opts.packageLabel);
      // Geen leverancier tonen: het leveranciersnetwerk is bedrijfsgevoelig.
      e.meta.textContent = bits.join(" · ");
      // Prijs per m³ direct onder de prijs: de engineprijs gedeeld door het
      // aantal kuub van dezelfde respons, afgerond op centen.
      if (e.perM3 && typeof fe.salePrice === "number" && fe.quantity > 0) {
        e.perM3.textContent = formatMoney(Math.round((fe.salePrice / fe.quantity) * 100) / 100, fe.currency) + " per m³";
        setHidden(e.perM3, false);
      }
      // Toon het prijsblok (thema-styling .product__price--holder mag dan gelden).
      setHidden(e.priceWrap, false);
    }

    // Afrekenen: e-mailveld + knop alleen tonen als de klant direct mag afrekenen.
    var canCheckout = isPriced && fe.canCheckout === true;
    // Leverdatum-veld: alleen bij afrekenen, met min/max UIT de engine-respons
    // (earliest/horizon). De frontend begrenst puur de keuze; de server valideert
    // 'm nogmaals bij checkout. Geen datum = geen wens (het veld is optioneel).
    if (e.deliveryField) {
      if (opts.calendar) opts.calendar.configure(canCheckout ? fe.deliveryDays || [] : []);
      setHidden(e.deliveryField, !canCheckout);
    }
    if (e.emailField) setHidden(e.emailField, !canCheckout);
    if (e.checkout) setHidden(e.checkout, !canCheckout);
    // Offerte-knop: tonen bij een handmatige offerte (incl. landelijke fallback) of
    // bij een prijs die toch een offerte vereist.
    // "error" staat hier bewust bij. Voorheen verdween de offerteknop juist als de
    // engine niet antwoordde, en hield de bezoeker geen enkele route over: geen prijs,
    // geen offerte. Een storing van de prijsengine mag geen doodlopende pagina opleveren.
    // Hiermee wijkt dit bestand af van de bron waaruit het is geport; dat is een
    // bewuste keuze, geen drift.
    if (e.quote) {
      setHidden(
        e.quote,
        !(fe.status === "manual_quote_required" || fe.status === "error" || (isPriced && !canCheckout))
      );
    }

    // Line-item properties alleen bij direct afrekenen.
    setMetadata(e.metadata, canCheckout ? opts.lineItemProperties : null);
  }

  function init(root) {
    var apiUrl = root.getAttribute("data-api-url");
    var checkoutUrl = root.getAttribute("data-checkout-url");
    var site = root.getAttribute("data-site");
    var productSlug = root.getAttribute("data-product-slug");
    var currency = root.getAttribute("data-currency") || "EUR";
    var pageType = pageTypeOf(root);
    var e = els(root);
    // De grenzen van het kuubveld (data-jtw-min / data-jtw-max in de snippet).
    var qtyMin = quantityBound(e.quantity, "data-jtw-min", 1);
    var qtyMax = quantityBound(e.quantity, "data-jtw-max", 50);

    /**
     * De laatste uitkomst op de root, voor het offerteformulier: bij een klik op
     * een offertelink leest gs-offerte-context het quote_id van de gelogde
     * berekening, de status en de redencode, zodat de aanvraag in het control
     * center aan die berekening hangt. Dit quote_id geeft geen afrekenrecht:
     * afrekenen loopt alleen via currentQuoteId (can_checkout). Wijzigt de
     * invoer, dan gaat alles weg.
     */
    function zetUitkomst(quoteId, status, reden) {
      [["data-jtw-quote-id", quoteId], ["data-jtw-uitkomst", status], ["data-jtw-reden", reden]].forEach(function (paar) {
        if (paar[1] === null || paar[1] === undefined || paar[1] === "") root.removeAttribute(paar[0]);
        else root.setAttribute(paar[0], String(paar[1]));
      });
    }
    // De quote_id hoort ALTIJD bij de op dit moment getoonde, afrekenbare prijs.
    // null = niets afrekenbaars getoond (stale-quote-vangnet).
    var currentQuoteId = null;
    // De checkoutsleutel bij die quote_id (quote_key uit dezelfde calculate-response).
    // Gaat mee naar de checkout; vervalt samen met de quote_id.
    var currentQuoteKey = null;
    // Meting: de laatst gemelde postcode, zodat hetzelfde gebied niet bij elke
    // blur opnieuw een `postcode_ingevuld` oplevert.
    var reportedPostcode = null;
    // Meting: de gegevens van de laatst getoonde, afrekenbare prijs. Nodig voor
    // `bestelling_aangevraagd`, want bij het afrekenen zelf is alleen de
    // quote_id nog in de hand. Vervalt samen met die quote_id.
    var lastPricedSnapshot = null;

    /** Publiceer een gebeurtenis van deze calculator, met het paginatype erbij. */
    function trackHere(eventName, params) {
      params.pagina_type = pageType;
      track(eventName, params);
    }

    /** Meld een geldig ingevulde postcode — alleen de vier cijfers. */
    function trackPostcode(pc4) {
      if (!pc4 || pc4 === reportedPostcode) return;
      reportedPostcode = pc4;
      trackHere("postcode_ingevuld", { postcode_regio: pc4, product: productSlug });
    }

    /**
     * Publiceer een uitkomst-event hoogstens één keer per product + postcode + m3 +
     * verpakking (zie alreadyReported).
     */
    function trackOnce(eventName, pc4, quantity, packageType, reden) {
      if (alreadyReported(eventName, productSlug, pc4, quantity, packageType)) return;
      trackHere(eventName, { postcode_regio: pc4, product: productSlug, reden: reden });
    }

    /** Meld dat deze postcode geen automatische prijs opleverde. */
    function trackNoDelivery(pc4, quantity, packageType, reden) {
      trackOnce("postcode_geen_levering", pc4, quantity, packageType, reden);
    }

    /** Meld een offerte op maat (knijper, Waddeneilanden, boven het maximum, product op offerte, op aanvraag). */
    function trackOfferteOpMaat(pc4, quantity, packageType, reden) {
      trackOnce("offerte_op_maat", pc4, quantity, packageType, reden);
    }

    /** Meld een technische fout (engine_fout_<status> of engine_onbereikbaar). */
    function trackCalculatorFout(pc4, quantity, packageType, reden) {
      trackOnce("calculator_fout", pc4, quantity, packageType, reden);
    }

    /**
     * Kies het uitkomst-event bij een handmatige offerte; onbekend of leeg is geen
     * offerte op maat. Meldt de engine opAanvraag=true, dan is de reden altijd
     * `op_aanvraag`, ongeacht de reasonCode.
     */
    function trackOfferte(pc4, quantity, packageType, reden, opAanvraag) {
      if (opAanvraag === true) trackOfferteOpMaat(pc4, quantity, packageType, "op_aanvraag");
      else if (OFFERTE_OP_MAAT_CODES.indexOf(reden) === -1) trackNoDelivery(pc4, quantity, packageType, reden);
      else trackOfferteOpMaat(pc4, quantity, packageType, reden);
    }
    // Eigen kalender-widget voor de leverdatum (één per calculator).
    var calendar = createCalendar(e, function () {
      setDeliveryError(e, "");
    });
    // Beide spinners hebben eigen display-CSS; bij init expliciet uit zodat ze niet
    // al bij het laden van de pagina lijken te draaien.
    setLoading(e, false);
    setCheckoutLoading(e, false);

    function showFieldError(msg) {
      if (e.postcodeError) {
        e.postcodeError.textContent = msg;
        e.postcodeError.hidden = !msg;
        e.postcodeError.classList.toggle("jtw-price-calculator__hint--error", !!msg);
      }
      e.postcode.setAttribute("aria-invalid", msg ? "true" : "false");
    }

    /**
     * Stale-quote-vangnet: zodra de klant de invoer wijzigt hoort de oude prijs
     * (en dus de oude quote_id) er niet meer bij. Wis de quote_id en verberg het
     * e-mailveld + de afrekenknop tot er een nieuwe can_checkout=true-berekening is.
     */
    function invalidateCheckout() {
      currentQuoteId = null;
      currentQuoteKey = null;
      lastPricedSnapshot = null;
      zetUitkomst(null, null, null);
      setCheckoutLoading(e, false);
      if (e.deliveryField) setHidden(e.deliveryField, true);
      calendar.reset();
      setDeliveryError(e, "");
      if (e.emailField) setHidden(e.emailField, true);
      if (e.checkout) setHidden(e.checkout, true);
      setEmailError(e, "");
    }

    // Volgnummer van de laatste berekening. Een antwoord dat binnenkomt nadat de
    // invoer al gewijzigd is, hoort bij oude invoer en wordt genegeerd.
    var calcSeq = 0;

    /**
     * Elke invoerwijziging (typen, kiezen, de plus/min-knoppen, de m³-hulp) maakt
     * de vorige uitkomst direct ongeldig: de veldfout onder de postcode, het
     * resultaatblok en (via invalidateCheckout) de quote_id en de afrekenvelden
     * gaan weg, en een berekening die nog onderweg is telt niet meer. Zo staat er
     * nooit een prijs of melding in beeld die bij andere invoer hoort.
     */
    function invalidateOutcome() {
      calcSeq += 1;
      setLoading(e, false);
      invalidateCheckout();
      showFieldError("");
      resetResult(e);
      e.message.textContent = "";
      e.result.hidden = true;
    }
    /**
     * Een knijper kan alleen bij los gestort (besluit eigenaar 13-10-2026). Bij een
     * andere verpakking (of zonder verpakkingskeuze, dan geldt big bag) is het vinkje
     * verborgen en uit; het verzoek stuurt dan altijd knijper=false.
     */
    function knijperMogelijk() {
      return (e.pkg ? e.pkg.value : "big_bag") === "los_gestort";
    }
    function syncKnijper() {
      var mogelijk = knijperMogelijk();
      if (e.knijperField) e.knijperField.hidden = !mogelijk;
      if (!mogelijk && e.knijper) e.knijper.checked = false;
    }
    syncKnijper();
    if (e.pkg) e.pkg.addEventListener("change", syncKnijper);
    [e.postcode, e.quantity, e.pkg, e.knijper].forEach(function (field) {
      if (!field) return;
      field.addEventListener("input", invalidateOutcome);
      field.addEventListener("change", invalidateOutcome);
    });

    // Meting: een geldige postcode melden zodra de klant het veld verlaat. De
    // berekenknop meldt 'm ook, voor wie meteen doorklikt; trackPostcode houdt
    // het bij één gebeurtenis per postcode.
    if (e.postcode) {
      e.postcode.addEventListener("change", function () {
        var ingevuld = normalizePostcode(e.postcode.value);
        if (ingevuld.valid) trackPostcode(ingevuld.pc4);
      });
    }

    // m³-hulp: oppervlakte(m²) × laagdikte(cm) / 100 -> vult het kuub-veld.
    // Puur front-end rekenwerk; de prijs komt daarna nog van de backend. Geen
    // bovengrens: boven het maximum van het kuubveld gaat het echte aantal mee
    // naar het offerteformulier.
    if (e.m3Area && e.m3Depth && e.quantity) {
      var m3nf = new Intl.NumberFormat("nl-NL", { maximumFractionDigits: 1 });
      var lastM3 = 0;
      var applyLabel = e.m3Apply ? e.m3Apply.textContent.trim() : "";

      /** De uitkomst in hele kuubs, naar BOVEN afgerond (nooit te weinig materiaal). */
      function neededQuantity() {
        return snapQuantity(lastM3, qtyMin);
      }

      function recalcM3() {
        var area = parseDecimal(e.m3Area.value);
        var depth = parseDecimal(e.m3Depth.value);
        if (!(area > 0) || !(depth > 0)) {
          lastM3 = 0;
          if (e.m3Result) setHidden(e.m3Result, true);
          return;
        }
        lastM3 = area * (depth / 100);
        if (e.m3Value) e.m3Value.textContent = m3nf.format(lastM3);
        var n = neededQuantity();
        var boven = n > qtyMax;
        if (e.m3Over) {
          e.m3Over.textContent = boven ? "Boven " + qtyLabel(qtyMax) + " m³ maken wij een offerte op maat." : "";
          setHidden(e.m3Over, !boven);
        }
        if (e.m3Apply) e.m3Apply.textContent = boven ? "Vraag een offerte aan voor " + qtyLabel(n) + " m³" : applyLabel;
        if (e.m3Result) setHidden(e.m3Result, false);
      }

      e.m3Area.addEventListener("input", recalcM3);
      e.m3Depth.addEventListener("input", recalcM3);

      if (e.m3Apply) {
        e.m3Apply.addEventListener("click", function () {
          if (!lastM3) return;
          var n = neededQuantity();
          e.quantity.value = String(n);
          // change-event: de prijscalculator vervalt de oude (stale) quote en
          // toont straks de prijs voor dit nieuwe aantal.
          e.quantity.dispatchEvent(new Event("change", { bubbles: true }));
          if (e.m3Details) e.m3Details.open = false;
          // Boven het maximum: door naar het offerteformulier. Via een klik op de
          // offertelink, zodat gs-offerte-context postcode, m3 en verpakking
          // meeneemt en de browser naar het formulier gaat.
          if (n > qtyMax && e.quote) {
            // Geen engine-berekening, wel dezelfde vaste code die de engine boven
            // het maximum geeft: zo kent de aanvraag de reden van de offerte.
            zetUitkomst(null, "manual_quote_required", "boven_max_m3");
            e.quote.click();
            return;
          }
          e.quantity.focus();
        });
      }
    }


    /* ---------------- hoeveelheid: ladder, knoppen en correctie --------------
     * In een eigen functie, zodat een plek ZONDER hoeveelheidsveld alleen dit
     * stuk overslaat en de rest van de calculator gewoon blijft werken. Geeft
     * de correctie terug, voor de knop Bereken prijs (null zonder veld).
     * ---------------------------------------------------------------------- */
    function setupQuantityLadder() {
      if (!e.quantity) return null;

      /** Korte toelichting onder het veld; leeg = weg. */
      function setQuantityNote(msg) {
        if (!e.quantityNote) return;
        e.quantityNote.textContent = msg || "";
        setHidden(e.quantityNote, !msg);
      }

      /** De min-knop uit op het minimum: een knop die niets doet is erger dan geen knop. */
      function syncQtyButtons() {
        var q = parseDecimal(e.quantity.value);
        if (e.qtyDown) e.qtyDown.disabled = isFinite(q) && q <= qtyMin;
      }

      /** Zet een waarde en meld de wijziging aan de rest van het scherm. */
      function setQuantity(q) {
        e.quantity.value = String(q);
        syncQtyButtons();
        invalidateOutcome();
      }

      /**
       * Trek de getypte waarde naar de ladder, zet de uitleg eronder en geef het
       * aantal terug. Staat er al precies dat getal, dan blijft het veld staan.
       */
      function applyCorrection(vanKnop) {
        var c = correctQuantity(e.quantity.value, qtyMin);
        if (e.quantity.value !== String(c.value)) setQuantity(c.value);
        else syncQtyButtons();
        // De knop Bereken prijs zet alleen een nieuwe uitleg. De uitleg van een
        // eerdere correctie (bij het verlaten van het veld) blijft dan staan,
        // want die hoort bij het aantal waarvoor de prijs nu komt.
        if (c.note || !vanKnop) setQuantityNote(c.note);
        return c.value;
      }

      if (e.qtyUp) {
        e.qtyUp.addEventListener("click", function () {
          setQuantity(quantityUp(parseDecimal(e.quantity.value), qtyMin));
          setQuantityNote("");
        });
      }
      if (e.qtyDown) {
        e.qtyDown.addEventListener("click", function () {
          setQuantity(quantityDown(parseDecimal(e.quantity.value), qtyMin));
          setQuantityNote("");
        });
      }

      // Typen haalt de oude uitleg weg. Bij het verlaten van het veld (change,
      // dat pas na het laatste input-event komt) wordt de waarde naar de ladder
      // getrokken en blijft de uitleg van die correctie staan.
      e.quantity.addEventListener("input", function () {
        setQuantityNote("");
      });
      e.quantity.addEventListener("change", function () {
        // Verlaat de klant het veld door op Bereken prijs te drukken, dan zou de
        // uitleg boven de knop verschijnen: de knop schuift weg onder de muis of
        // vinger en de klik gaat verloren. De knop corrigeert dan zelf, als eerste.
        if (calcPressed) return;
        applyCorrection();
      });

      // Beginstand: een verkeerd ingestelde standaardwaarde in het thema mag de
      // klant niet met een onmogelijke hoeveelheid opzadelen.
      e.quantity.value = String(snapQuantity(parseDecimal(e.quantity.value), qtyMin));
      syncQtyButtons();
      return applyCorrection;
    }
    // Of Bereken prijs nu wordt ingedrukt (zie de change-luisteraar van het
    // kuubveld). Los bij de klik zelf, bij een afgebroken aanraking, en bij een
    // muis die elders loslaat (de klik volgt dan direct op pointerup). Bij
    // aanraking komen blur en klik pas na pointerup.
    var calcPressed = false;
    function releaseCalc() {
      calcPressed = false;
    }
    e.btn.addEventListener("pointerdown", function () {
      calcPressed = true;
    });
    document.addEventListener("click", releaseCalc, true);
    document.addEventListener("pointercancel", releaseCalc);
    document.addEventListener("pointerup", function (ev) {
      if (ev.pointerType !== "touch") setTimeout(releaseCalc, 0);
    });
    var applyQuantityCorrection = setupQuantityLadder();

    e.btn.addEventListener("click", function () {
      // Eerst de hoeveelheid, door dezelfde correctie als bij het verlaten van
      // het veld: een correctie wist de uitkomst en de veldfout.
      var quantity = applyQuantityCorrection ? applyQuantityCorrection(true) : qtyMin;
      showFieldError("");

      // 1. Client-side veldvalidatie (invalid_input): geen call als postcode fout is.
      var pc = normalizePostcode(e.postcode.value);
      if (!pc.valid) {
        showFieldError("Vul een geldige postcode in, bijvoorbeeld 1234 AB.");
        e.postcode.focus();
        return; // geen offerte/checkout automatisch starten
      }
      trackPostcode(pc.pc4);
      if (!apiUrl) {
        render(e, { status: "error", message: "Calculator is nog niet geconfigureerd (API-URL ontbreekt)." });
        return;
      }

      var packageType = e.pkg ? e.pkg.value : "big_bag";

      // 2. Loading-state. Wis eerst de volledige vorige uitkomst (geen oude prijs)
      //    en de oude quote_id; die wordt pas opnieuw gezet bij een afrekenbaar antwoord.
      //    Het volgnummer koppelt het antwoord aan deze invoer.
      calcSeq += 1;
      var seq = calcSeq;
      setLoading(e, true);
      currentQuoteId = null;
      currentQuoteKey = null;
      lastPricedSnapshot = null;
      zetUitkomst(null, null, null);
      resetResult(e);
      e.result.hidden = false;
      e.result.setAttribute("data-state", "loading");
      e.message.textContent = "Bezig met berekenen…";

      fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          site: site,
          product_slug: productSlug,
          postcode: pc.value,
          quantity: quantity,
          package_type: packageType,
          session_id: calculatorSessionId(),
          herkomst: calculatorHerkomst(),
          options: { knijper: knijperMogelijk() && !!(e.knijper && e.knijper.checked) },
        }),
      })
        .then(function (res) {
          return res.json().then(function (data) {
            return { ok: res.ok, status: res.status, data: data };
          });
        })
        .then(function (r) {
          // De invoer is intussen gewijzigd: dit antwoord hoort bij oude invoer.
          if (seq !== calcSeq) return;
          // Lees het camelCase frontend-contract; val terug op een nette fout.
          var fe = r.data && r.data.frontend;
          if (!fe) {
            render(e, { status: "error", message: "Onverwacht antwoord van de server." });
            zetUitkomst(null, "error", "engine_fout_" + r.status);
            trackCalculatorFout(pc.pc4, quantity, packageType, "engine_fout_" + r.status);
            return;
          }
          render(e, fe, {
            packageLabel: packageLabel(packageType),
            lineItemProperties: r.data.line_item_properties,
            calendar: calendar,
          });
          // Quote_id-bron = de top-level quote_id van DEZE calculate-response. De
          // server geeft hem bij elke gelogde berekening; voor afrekenen alleen
          // bewaren bij can_checkout=true (een offerte-quote weigert de checkout).
          var checkoutable = fe.status === "priced" && fe.canCheckout === true;
          currentQuoteId = checkoutable ? r.data.quote_id || null : null;
          currentQuoteKey = currentQuoteId ? r.data.quote_key || null : null;
          zetUitkomst(
            typeof r.data.quote_id === "number" ? r.data.quote_id : null,
            fe.status,
            fe.status === "manual_quote_required" ? reasonFor(fe) : !r.ok ? "engine_fout_" + r.status : null
          );
          // invalid_input: ook het postcodeveld markeren.
          if (fe.status === "invalid_input") showFieldError(fe.message);

          // --- Meting -------------------------------------------------------
          // render() hierboven heeft de uitkomst al op het scherm gezet, dus dit
          // is het moment om 'm te melden. Een afrekenbare prijs bewaren we ook
          // voor `bestelling_aangevraagd`.
          lastPricedSnapshot = checkoutable
            ? { pc4: pc.pc4, quantity: fe.quantity, salePrice: fe.salePrice }
            : null;
          if (fe.status === "priced") {
            if (typeof fe.salePrice === "number" && !alreadyReported("prijs_getoond", productSlug, pc.pc4, fe.quantity, packageType)) {
              var priceParams = {
                postcode_regio: pc.pc4,
                product: productSlug,
                hoeveelheid_m3: fe.quantity,
                prijs: fe.salePrice,
                verpakking: packageType,
              };
              var zone = zoneLabel(fe.postcodeArea);
              if (zone) priceParams.zone = zone;
              trackHere("prijs_getoond", priceParams);
            }
          } else if (fe.status === "manual_quote_required") {
            trackOfferte(pc.pc4, quantity, packageType, reasonFor(fe), fe.opAanvraag);
          } else if (!r.ok) {
            // 400/500/503: de engine antwoordde wel, maar met een fout.
            trackCalculatorFout(pc.pc4, quantity, packageType, "engine_fout_" + r.status);
          }
        })
        .catch(function () {
          if (seq !== calcSeq) return;
          render(e, { status: "error", message: "De prijs kon niet worden opgehaald. Probeer het later opnieuw." });
          zetUitkomst(null, "error", "engine_onbereikbaar");
          // Meting: de engine was onbereikbaar of antwoordde geen JSON (bv. een
          // 503-foutpagina): een technische fout, geen uitkomst van de engine.
          trackCalculatorFout(pc.pc4, quantity, packageType, "engine_onbereikbaar");
        })
        .finally(function () {
          if (seq === calcSeq) setLoading(e, false);
        });
    });

    // Afrekenen (draft order): vraag e-mail, POST { quote_id, email } naar de
    // checkout-URL en stuur de klant door naar de invoice_url. We sturen NOOIT
    // leverancier/inkoop/marge mee; de quote_id resolvet server-side.
    if (e.checkout) {
      e.checkout.addEventListener("click", function () {
        setEmailError(e, "");
        setDeliveryError(e, "");

        // Stale-quote-vangnet: zonder geldige, actuele quote eerst opnieuw berekenen.
        if (!currentQuoteId) {
          setEmailError(e, "Bereken eerst (opnieuw) uw prijs.");
          return;
        }
        if (!checkoutUrl) {
          setEmailError(e, "Afrekenen is nog niet geconfigureerd.");
          return;
        }

        var email = e.email ? String(e.email.value || "").trim() : "";
        if (!isValidEmail(email)) {
          setEmailError(e, "Vul een geldig e-mailadres in.");
          if (e.email) e.email.focus();
          return;
        }

        // Gewenste leverdatum is OPTIONEEL: alleen meesturen als de klant er een
        // koos. De server valideert 'm server-side (bron van waarheid).
        var desiredDate = e.deliveryDate ? String(e.deliveryDate.value || "").trim() : "";
        var payload = { quote_id: currentQuoteId, email: email };
        if (currentQuoteKey) payload.quote_key = currentQuoteKey;
        if (desiredDate) payload.desired_delivery_date = desiredDate;

        setCheckoutLoading(e, true);

        fetch(checkoutUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        })
          .then(function (res) {
            return res
              .json()
              .then(function (data) {
                return { status: res.status, ok: res.ok, data: data };
              })
              .catch(function () {
                return { status: res.status, ok: res.ok, data: null };
              });
          })
          .then(function (r) {
            // Succes: { status: "checkout_ready", invoice_url } -> doorsturen.
            if (r.ok && r.data && r.data.invoice_url) {
              // Meting vóór de redirect: hierna verlaat de bezoeker deze pagina.
              if (lastPricedSnapshot) {
                trackHere("bestelling_aangevraagd", {
                  product: productSlug,
                  hoeveelheid_m3: lastPricedSnapshot.quantity,
                  prijs: lastPricedSnapshot.salePrice,
                  postcode_regio: lastPricedSnapshot.pc4,
                });
              }
              window.location = r.data.invoice_url;
              return;
            }
            // Verlopen/onbekende quote: deze quote is niet meer bruikbaar. Wis 'm
            // (knop blokkeert hierna tot er opnieuw berekend is) maar laat het
            // e-mailveld + de melding staan zodat de klant de actie kan lezen.
            // Afgekeurde leverdatum (server-side validatie): toon de melding bij het
            // datumveld i.p.v. bij het e-mailveld, met de server-tekst.
            if (r.status === 400 && r.data && r.data.field === "desired_delivery_date") {
              setDeliveryError(e, r.data.error || "Kies een andere leverdatum.");
              if (e.deliveryDate) e.deliveryDate.focus();
              return;
            }
            if (r.status === 410 || r.status === 404) {
              currentQuoteId = null;
              currentQuoteKey = null;
            }
            setEmailError(e, checkoutErrorMessage(r.status));
          })
          .catch(function () {
            setEmailError(e, "Afrekenen lukte niet. Probeer het later opnieuw.");
          })
          .finally(function () {
            setCheckoutLoading(e, false);
          });
      });
    }
  }

  function boot() {
    document.querySelectorAll("[data-jtw-calculator]").forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
